import {
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  useRouter,
} from 'expo-router';
import * as WebBrowser from 'expo-web-browser';
import React, { useState } from 'react';
import { ScrollView, View } from 'react-native';
import Toast from 'react-native-toast-message';
import {
  createStyleSheet,
  UnistylesRuntime,
  useStyles,
} from 'react-native-unistyles';

import { useConnectInsuranceSession } from '@/api/customers/use-connect-insurance-session';
import type { CreateInsuranceSessionResponse } from '@/api/customers/use-create-insurance-session';
import { useCreateInsuranceSession } from '@/api/customers/use-create-insurance-session';
import { useValidateInsurance } from '@/api/customers/use-validate-insurance';
import { useReservation } from '@/api/reservations/use-reservation';
import VerificationScreen from '@/components/verification-screen';
import { Button, showErrorMessage, Text } from '@/ui';
import { Icon } from '@/ui/icon';
import { Docs } from '@/ui/icons';
import { ListItem } from '@/ui/list-item';
import { Loader } from '@/ui/loader';

import BackButton from '../back-button';
import Footer from '../footer';
import { NavigationButton, NavigationButtonTypes } from '../navigation-button';

interface CreateSession {
  ignitionToken: string;
  ignitionUri: string;
}

interface InsuranceStatus {
  is_insurance_connected: boolean;
  is_insurance_valid: boolean;
}

enum InsuranceStatusEnum {
  Complete = 'complete',
  Exit = 'exit',
  Error = 'error',
}

enum InsuranceResultEnum {
  Link = 'link',
  Basic = 'basic',
  Manual = 'manual',
}

interface InsuranceEvent {
  status: InsuranceStatusEnum;
  authCode: string;
  client: string;
  result: InsuranceResultEnum;
  step: string;
  message: string;
}

export default function VerifyInsurance() {
  const { styles, theme } = useStyles(stylesheet);
  const router = useRouter();
  const { trip_id, authCode, status: axleStatus } = useLocalSearchParams();
  const [status, setStatus] = React.useState<
    'uploaded' | 'successful' | 'rejected' | ''
  >('');
  const [axleUri, setAxleUri] = React.useState('');
  const [showInsuranceIFrame, setShowInsuranceIFrame] = useState(false);
  const [insuranceIFrameSrc, setInsuranceIFrameSrc] = useState('');
  const [loadedIFrame, setLoadedIFrame] = useState(false);

  const { data: resData, isPending: isFetchingReservation } = useReservation({
    //@ts-ignore
    variables: { id: trip_id },
    enabled: !!trip_id,
  });

  const {
    mutateAsync: createInsuranceSession,
    isPending: isCreatingInsuranceSession,
    isError: isCreateInsuranceSessionError,
    error: createInsuranceSessionError,
  } = useCreateInsuranceSession();

  const {
    mutateAsync: connectInsuranceSession,
    isPending: isConnectingSession,
    isError: isConnectInsuranceSessionError,
    error: connectInsuranceSessionError,
    isSuccess: isConnectInsuranceSessionSuccess,
  } = useConnectInsuranceSession();

  const {
    mutateAsync: validateInsurance,
    isPending: isValidatingInsurance,
    isError: isValidateInsuranceError,
    error: validateInsuranceError,
    isSuccess: isValidateInsuranceSuccess,
  } = useValidateInsurance();

  React.useEffect(() => {
    if (!axleUri && !authCode) return;

    const openAxleUri = async () => {
      window.open(axleUri, '_blank');
    };

    openAxleUri();
  }, [axleUri]);

  useFocusEffect(
    React.useCallback(() => {
      setStatus('');
    }, [])
  );

  React.useEffect(() => {
    if (axleStatus === 'complete' && authCode) {
      const handleConnectInsuranceSession = async () => {
        await WebBrowser.dismissBrowser();

        try {
          await connectInsuranceSession({ authCode: authCode as string });
        } catch (e) {
          console.log('connect', e);
          setStatus('rejected');
        }
      };

      handleConnectInsuranceSession();
    }
  }, [axleStatus, authCode, connectInsuranceSession]);

  const handleInsuranceValidation = () => {
    const handleValidateInsurance = async () => {
      try {
        const {
          message: successMsg,
          data: { manualInsuranceStatus },
        } = await validateInsurance({
          reservationId: parseInt(trip_id as string),
        });

        Toast.show({
          text1: successMsg || 'Your insurance policy has been verified',
          type: 'success',
        });

        setStatus(
          manualInsuranceStatus === 'verified' ? 'successful' : 'uploaded'
        );
      } catch (error: any) {
        console.log('validate', error);
        setStatus('rejected');
      }
    };

    handleValidateInsurance();
  };

  const handleConnectInsuranceAccount = (data: InsuranceEvent) => {
    const handleCreateInsuranceSession = async () => {
      try {
        await connectInsuranceSession({ authCode: data.authCode as string });
      } catch (error: any) {
        console.error('Error fetching data:', error);
      }
    };

    handleCreateInsuranceSession();
  };

  React.useEffect(() => {
    if (!isConnectInsuranceSessionSuccess) return;

    handleInsuranceValidation();
  }, [isConnectInsuranceSessionSuccess]);

  React.useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (!event.origin.includes('axle.insure')) return;

      const data = event.data as InsuranceEvent;

      console.log(data);

      setShowInsuranceIFrame(false);

      if (data.status === InsuranceStatusEnum.Error) {
        showErrorMessage(data.message);
        return;
      }

      if (data.status === InsuranceStatusEnum.Exit) {
        showErrorMessage(
          'You need to upload your insurance information before you can check in for any trips'
        );
        return;
      }

      if (
        [InsuranceResultEnum.Manual, InsuranceResultEnum.Link].includes(
          data.result
        )
      ) {
        handleConnectInsuranceAccount(data);
      }

      // TODO: no clue what to do about InsuranceResult.Basic
    };

    window.addEventListener('message', handleMessage);

    // Clean up the event listener when the component unmounts
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  const handleVerifyInsurance = async () => {
    try {
      const redirectUri = `${window.location.origin}/account/verify-insurance?trip_id=${trip_id}`;

      const { ignitionUri }: CreateInsuranceSessionResponse =
        await createInsuranceSession({ redirectUri: redirectUri });

      const url = new URL(ignitionUri);
      url.searchParams.append('origin', window.location.origin);
      const uriWithQueryString = url.toString();

      setInsuranceIFrameSrc(uriWithQueryString);
      setShowInsuranceIFrame(true);

      // setAxleUri(`${ignitionUri}&redirectUri=${redirectUri}`);
    } catch (error) {
      console.error(error);
    }
  };

  if (resData && status !== '') {
    return (
      <VerificationScreen
        status={status}
        name="insurance"
        onGoBack={() => {
          if (trip_id) {
            router.navigate(`/trips/${trip_id}/details`);
          } else {
            router.navigate('/trips');
          }
        }}
        errorMsg={
          validateInsuranceError?.message ||
          connectInsuranceSessionError?.message
        }
      />
    );
  }

  if (
    isFetchingReservation ||
    loadedIFrame ||
    isValidatingInsurance ||
    isConnectingSession
  ) {
    return <Loader />;
  }

  return (
    <ScrollView
      contentContainerStyle={{
        flexGrow: 1,
        gap: 32,
        alignItems: 'center',
      }}
      showsVerticalScrollIndicator={false}
    >
      <View style={styles.container}>
        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <Stack.Screen
            options={{
              headerShown: false,
            }}
          />
        ) : (
          <Stack.Screen
            options={{
              title: 'Verify Your Insurance',
              headerTitle: 'Verify Your Insurance',
              headerLeft: () => (
                <BackButton
                  onPress={() => {
                    if (trip_id) {
                      router.navigate(`/trips/${trip_id}/details`);
                    } else {
                      router.navigate('/trips');
                    }
                  }}
                />
              ),
              headerShown: true,
              headerStyle: {
                backgroundColor: theme.colors.background_1,
              },
              headerTintColor: theme.colors.text.primary,
              headerShadowVisible: false,
              headerTitleStyle: {
                fontSize: 17,
              },
            }}
          />
        )}

        {['md', 'lg'].includes(UnistylesRuntime.breakpoint) ? (
          <View style={{ gap: 16 }}>
            <NavigationButton
              type={NavigationButtonTypes.back_with_text}
              onPress={() => {
                router.back();
              }}
            />

            <Text type="largeTitle">Verify Your Insurance</Text>
          </View>
        ) : null}

        {showInsuranceIFrame ? (
          <iframe
            src={insuranceIFrameSrc}
            title="Insurance Session"
            style={{
              marginTop: 16,
              width: '100%',
              height: '90%',
            }}
            onLoadStart={() => {
              setLoadedIFrame(true);
            }}
            onLoad={() => {
              setLoadedIFrame(false);
            }}
          />
        ) : (
          <View
            style={{
              flexGrow: 1,
              justifyContent: 'space-between',
              padding: 16,
            }}
          >
            <View style={{ gap: 24 }}>
              <View>
                <Text
                  type="bodyRegular"
                  style={{ color: theme.colors.text.secondary }}
                >
                  To be approved to drive, you must verify your insurance.
                  Verification typically takes just a few minutes.
                </Text>
              </View>

              <View>
                <ListItem
                  text="Own insurance"
                  caption="If you’re using your own insurance policy, we’ll securely verify it with Axle to ensure you’re good to go."
                  icon={
                    <Icon
                      type="dressed"
                      icon={<Docs />}
                      fillColor={theme.colors.icon.color}
                      width={25}
                      height={25}
                    />
                  }
                  onPress={() => {}}
                  withBorder={false}
                />
              </View>
            </View>

            <View>
              <Button
                text="Verify Insurance"
                type="primary"
                onPress={handleVerifyInsurance}
                disabled={isCreatingInsuranceSession}
                loading={isCreatingInsuranceSession}
              />
            </View>
          </View>
        )}
      </View>

      <Footer />
    </ScrollView>
  );
}

const stylesheet = createStyleSheet((theme) => ({
  container: {
    flexGrow: 1,
    backgroundColor: theme.colors.background_1,
    width: {
      md: 600,
    },
    height: {
      md: 800,
    },
  },
}));
